import React from 'react';
import PropTypes from 'prop-types';
import { DynamicField } from '@totalsoft/rocket-ui';
import { getDynamicFieldControlType } from 'utils/functions';
import { defaultDecimalPlaces, defaultNumericPlaces } from 'constants/numeric';
import { emptyString } from 'constants/emptyConst';
import PhoneNumberField from 'components/common/inputs/PhoneNumberTextField';
import { testMobilePhone } from 'utils/simple-validations';

const PhoneNumberFieldComponent = ({
  column,
  item,
  index,
  onPropertyChange,
  clientExtraInformationWrite,
  countryCode,
  sectionIdx,
  t,
  rights
}) => {
  return {
    Component: PhoneNumberField,
    ComponentProps: {
      onChange: e => onPropertyChange(index, column.propertyName, e, sectionIdx),
      disabled: rights.readOnlyFull || !clientExtraInformationWrite,
      value: item[column.propertyName],
      error:
        (!testMobilePhone(String(item[column.propertyName]), countryCode) && item[column.propertyName]) ||
        (column.required && !item[column.propertyName]),
      helperText:
        !testMobilePhone(String(item[column.propertyName]), countryCode) && item[column.propertyName]
          ? t('Validations.Generic.Phone')
          : column.required && !item[column.propertyName]
          ? t('Validations.Generic.Mandatory')
          : emptyString,
      country: countryCode
    }
  };
};

const CustomComponents = ({ column, item, index, onPropertyChange, clientExtraInformationWrite, countryCode, sectionIdx, t, rights }) => {
  const { propertyType } = column;

  switch (propertyType) {
    case 'PHONE_NUMBER':
      return PhoneNumberFieldComponent({
        column,
        item,
        index,
        onPropertyChange,
        clientExtraInformationWrite,
        countryCode,
        sectionIdx,
        t,
        rights
      });
    default:
      return null;
  }
};

const DocumentPartnerExtraInformationWrapper = ({
  column,
  item,
  index,
  onPropertyChange,
  clientExtraInformationWrite,
  countryCode,
  sectionIdx,
  t,
  rights,
  loadPropertyDetails
}) => {
  const customComponent = CustomComponents({
    column,
    item,
    index,
    onPropertyChange,
    clientExtraInformationWrite,
    countryCode,
    sectionIdx,
    t,
    rights
  });

  return (
    <DynamicField
      id={`extraInfo_${index}_${column.propertyName}`}
      controlType={getDynamicFieldControlType(column.propertyType)}
      fullWidth
      onChange={e => onPropertyChange(index, column.propertyName, e, sectionIdx)}
      value={item[column.propertyName]}
      valueKey='id'
      labelKey='value'
      disabled={rights.readOnlyFull || !clientExtraInformationWrite}
      required={column.required && !rights.readOnlyFull}
      error={column.required && !item[column.propertyName] && !rights.readOnlyFull}
      helperText={column.required && !item[column.propertyName] ? t('Validations.Generic.Mandatory') : ''}
      inputProps={{
        decimalScale: column.propertyType === 'INT' ? defaultNumericPlaces : defaultDecimalPlaces
      }}
      simpleValue
      CustomComponent={customComponent?.Component}
      customComponentProps={customComponent?.ComponentProps}
      loadOptions={() => loadPropertyDetails(column.propertyCode)}
      isClearable={column.propertyType === 'ADAPTIVE_LOOKUP' || column.propertyType === 'TP_DATE'}
      sx={{ marginBottom: 1, marginTop: 1, display: 'block', marginLeft: '0.5rem' }}
    />
  );
};

DocumentPartnerExtraInformationWrapper.propTypes = {
  column: PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    propertyType: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    propertyCode: PropTypes.string.isRequired
  }).isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  clientExtraInformationWrite: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired,
  rights: PropTypes.object,
  loadPropertyDetails: PropTypes.func.isRequired
};

PhoneNumberFieldComponent.propTypes = {
  column: PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    propertyType: PropTypes.string.isRequired,
    caption: PropTypes.string.isRequired,
    required: PropTypes.bool.isRequired,
    propertyCode: PropTypes.string.isRequired
  }),
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  clientExtraInformationWrite: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired,
  rights: PropTypes.object
};

CustomComponents.propTypes = {
  column: PropTypes.shape({
    propertyType: PropTypes.string.isRequired
  }),
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  clientExtraInformationWrite: PropTypes.bool.isRequired,
  countryCode: PropTypes.string,
  sectionIdx: PropTypes.number,
  t: PropTypes.func.isRequired,
  rights: PropTypes.object
};

export default DocumentPartnerExtraInformationWrapper;
